import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { imageCache } from '../../../utils/imageCache';
import {
  setProposalStatus,
  setProposalSelectedGroup,
  setProposalAssetImages,
  setProposalAssetEdits,
  setProposalGroupEditAction,
  setProposalApprovedGroupId,
} from '../../../store/aiChatSlice';
import { setModalState, modalViewsConst, clearLastResult } from '../../../store/modalSlice';
import { AppDispatch, RootState } from '../../../store';
import { selectTotalAssetCount } from '../../../store/assetsSlice';
import { fetchSubscriptionStatus, selectSubscriptionStatus } from '../../../store/userSlice';
import { checkAssetLimit } from '../../../api/stripeSubscription';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AssetProposal, approveProposal, approveAddProposal } from './assetProposalUtils';
import { GroupEdit } from '../AssetProposal/AssetGroupEditor';

export interface AssetEdit {
  name: string;
  description: string;
  imageUrl: string | null;
  tempId: string;
}

// Normalize for name comparison: trim, lowercase, collapse internal whitespace.
function normalizeGroupName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

// Match the model's group-name hint to a real group by exact name, then a unique forward-substring
// (user typed a short form of a real name, e.g. "tire" → "Canadian Tire"). Precision over recall:
// anything ambiguous or absent resolves to undefined so we surface "not found" and let the user pick
// rather than silently selecting a different, similarly-named group.
function resolveGroupIdByName(groupList: { id: string; name: string }[], name: string | undefined): string | undefined {
  if (!name) return undefined;
  const needle = normalizeGroupName(name);
  if (!needle) return undefined;

  const entries = groupList.map((g) => ({ id: g.id, norm: normalizeGroupName(g.name ?? '') })).filter((g) => g.norm);

  // Exact — bail if duplicate names make it genuinely ambiguous.
  const exact = entries.filter((g) => g.norm === needle);
  if (exact.length === 1) return exact[0].id;
  if (exact.length > 1) return undefined;

  // Unique forward substring; guard on length 3+ so a 1–2 char name can't match by accident.
  if (needle.length < 3) return undefined;
  const partial = entries.filter((g) => g.norm.includes(needle));
  return partial.length === 1 ? partial[0].id : undefined;
}

// Decide the pre-selected group. A named-but-unmatched group resolves to '' so we never silently
// file assets into a different group; only a request that named no group defaults to the first one.
function pickGroupId(groupList: { id: string; name: string }[], savedGroupId: string | undefined, assetGroupName: string | undefined): string {
  if (savedGroupId) return savedGroupId;
  const byName = resolveGroupIdByName(groupList, assetGroupName);
  if (byName) return byName;
  if (assetGroupName?.trim()) return '';
  return groupList[0]?.id ?? '';
}

export function useAssetProposalState(proposal: AssetProposal, conversationId: string, messageId: string, disabled: boolean) {
  const dispatch = useDispatch<AppDispatch>();

  const status = useSelector((state: RootState) => state.aiChat.proposalStatuses[conversationId]?.[messageId]);
  const savedGroupId = useSelector((state: RootState) => state.aiChat.proposalSelectedGroups[conversationId]?.[messageId]);
  const groups = useSelector((state: RootState) => state.assetGroups.groups) as Record<string, { id: string; name: string; profilePictureUrl?: string }>;
  const groupList = Object.values(groups);
  const modalLastResult = useSelector((state: RootState) => state.modalState.lastResult);
  const modalProperties = useSelector((state: RootState) => state.modalState.properties);
  const savedAssetImages = useSelector((state: RootState) => state.aiChat.proposalAssetImages[conversationId]?.[messageId]);
  const savedAssetEdits = useSelector((state: RootState) => state.aiChat.proposalAssetEdits[conversationId]?.[messageId]);
  const savedGroupEdit = useSelector((state: RootState) => state.aiChat.proposalGroupEdit[conversationId]?.[messageId]);
  const approvedGroupId = useSelector((state: RootState) => state.aiChat.proposalApprovedGroupIds[conversationId]?.[messageId] ?? null);
  const totalAssetCount = useSelector(selectTotalAssetCount);
  const subscriptionStatus = useSelector(selectSubscriptionStatus);

  const isAddMode = !proposal.assetGroup;
  const isSettled = status === 'approved' || status === 'declined';

  // True once groups have loaded and the model named a group we can't match — surfaced in the UI
  // so the user picks (or creates) the right one instead of us silently defaulting to another group.
  const groupHintName = proposal.assetGroupName?.trim() ?? '';
  const groupHintNotFound = useMemo(
    () => isAddMode && !!groupHintName && groupList.length > 0 && !resolveGroupIdByName(groupList, groupHintName),
    // groupList.length keys the match: identity churn from unrelated Redux writes shouldn't re-run it.
    [isAddMode, groupHintName, groupList.length] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [selectedGroupId, setSelectedGroupId] = useState(() =>
    pickGroupId(groupList, savedGroupId, proposal.assetGroupName)
  );
  // Boolean: false when selectedGroupId is empty or points to a deleted group.
  // Used as an effect dep so the recovery fires on same-count group churn too.
  const selectedGroupExists = !selectedGroupId || !!groups[selectedGroupId];
  const [isGroupListOpen, setIsGroupListOpen] = useState(false);
  const [isAssetsOpen, setIsAssetsOpen] = useState(true);
  const [groupImageUrls, setGroupImageUrls] = useState<Record<string, string>>({});
  const [isGroupExpanded, setIsGroupExpanded] = useState(false);
  const [groupEdit, setGroupEdit] = useState<GroupEdit>(() => ({
    name: savedGroupEdit?.name ?? proposal.assetGroup?.name ?? '',
    description: savedGroupEdit?.description ?? proposal.assetGroup?.description ?? '',
  }));
  const [expandedAssetIndex, setExpandedAssetIndex] = useState<number | null>(null);
  // useRef instead of useState: this value is only read inside the modal-result
  // effect and written in the click handler — it never drives rendering, so a
  // ref keeps it live without adding it to any dependency array.
  const imageUploadTargetIndexRef = useRef<number | null>(null);
  // Cache Admin status once on mount (cognito:groups don't change mid-session) so the approve hot path doesn't await fetchAuthSession on every click. Fail open → leave false; the cap still gates non-admins.
  const isAdminRef = useRef(false);
  useEffect(() => {
    fetchAuthSession()
      .then((s) => {
        const groups = s?.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined;
        isAdminRef.current = groups?.includes('Admin') ?? false;
      })
      .catch(() => {});
  }, []);
  const [assetEdits, setAssetEdits] = useState<AssetEdit[]>(() =>
    proposal.assets.map((a, i) => ({
      name: savedAssetEdits?.[i]?.name ?? a.name,
      description: savedAssetEdits?.[i]?.description ?? a.description ?? a.type ?? '',
      imageUrl: savedAssetImages?.[i] ?? a.imageUrl ?? null,
      tempId: crypto.randomUUID(),
    }))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (groupList.length > 0 && (!selectedGroupId || !selectedGroupExists)) {
      // Re-run the pick now that groups have loaded/changed; a named-but-unmatched group stays ''
      // (forces a manual pick) rather than falling back to the first group. Ignore a saved id that
      // points to a since-deleted group so recovery doesn't just re-select the missing one.
      const validSavedGroupId = savedGroupId && groups[savedGroupId] ? savedGroupId : undefined;
      const next = pickGroupId(groupList, validSavedGroupId, proposal.assetGroupName);
      if (next !== selectedGroupId) setSelectedGroupId(next);
    }
    // groupList.length + selectedGroupExists are intentional: groupList.length
    // catches add/remove; selectedGroupExists catches the case where a group is
    // deleted and replaced by one of the same count (boolean flips false → true).
    // Using the groups object reference would re-run on every unrelated Redux write.
  }, [groupList.length, selectedGroupExists]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (disabled) {
      setExpandedAssetIndex(null);
      setIsGroupExpanded(false);
    }
  }, [disabled]);

  // Warm the shared subscription cache so the asset-limit check at approve time is instant (no per-click fetch).
  useEffect(() => {
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchImages = async () => {
      const entries = await Promise.all(
        groupList.map(async (g) => {
          if (!g?.profilePictureUrl) return null;
          try {
            const url = await imageCache.getSignedUrl(g.profilePictureUrl);
            return url ? [g.id, url] as const : null;
          } catch { return null; }
        })
      );
      if (!isMounted) return;
      const urls: Record<string, string> = {};
      entries.forEach((e) => { if (e) urls[e[0]] = e[1]; });
      setGroupImageUrls(urls);
    };
    fetchImages();
    return () => { isMounted = false; };
    // groupList.length is intentional: re-fetching signed URLs on every
    // object-reference update (e.g. unrelated Redux writes) would cause
    // excessive GCS requests. We only need to re-fetch when groups are
    // added or removed.
  }, [groupList.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!modalLastResult) return;
    if (modalLastResult.success && modalLastResult.imageUrl) {
      const newImageUrl = modalLastResult.imageUrl;
      imageCache.invalidate(newImageUrl);
      imageCache.getSignedUrl(newImageUrl);
      // Prefer the ref (always current); fall back to the index embedded in
      // the modal properties, which survives component unmount/remount because
      // closeModalWithResult does not clear state.modalState.properties.
      const targetIndex =
        imageUploadTargetIndexRef.current !== null
          ? imageUploadTargetIndexRef.current
          : (modalProperties?.__proposedAsset === true && modalProperties?.proposalMessageId === messageId
              ? (modalProperties?.proposalAssetIndex ?? null)
              : null);
      if (targetIndex !== null) {
        setAssetEdits((prev) => {
          const next = [...prev];
          next[targetIndex] = { ...next[targetIndex], imageUrl: newImageUrl };
          dispatch(setProposalAssetImages({ conversationId, messageId, images: next.map((e) => e.imageUrl) }));
          return next;
        });
        imageUploadTargetIndexRef.current = null;
        dispatch(clearLastResult());
      } else {
        // Index is unrecoverable — clear the stale result so it doesn't
        // get re-processed later, and surface an error so the user knows
        // the uploaded image was not applied.
        dispatch(clearLastResult());
        setSubmitError('Image upload could not be applied. Please try again.');
      }
    }
  }, [modalLastResult, modalProperties, conversationId, messageId, dispatch]);

  const handleOpenImageUpload = (index: number) => {
    imageUploadTargetIndexRef.current = index;
    const edit = assetEdits[index];
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.assetImageUpload,
      properties: {
        id: edit.tempId,
        name: edit.name,
        imageUrl: edit.imageUrl ?? null,
        __proposedAsset: true,
        proposalAssetIndex: index,
        proposalMessageId: messageId,
      },
    }));
  };

  const handleApprove = async () => {
    // Enforce the per-tier asset cap before creating anything (Admins are exempt, matching the
    // Home + group-page Add Asset flows). isAdmin is prefetched on mount; checkAssetLimit shares one cap.
    const isAdmin = isAdminRef.current;
    // Read from the prefetched Redux cache — null while loading or after a failed read means fail open.
    if (!isAdmin && subscriptionStatus) {
      const check = checkAssetLimit(subscriptionStatus, totalAssetCount, proposal.assets.length);
      if (!check.allowed) {
        dispatch(setModalState({
          modalOpen: true,
          modalView: modalViewsConst.assetLimitReached,
          properties: {
            __modalSize: 'xs',
            tier: check.tier,
            limit: check.limit,
            currentCount: check.currentCount,
            source: 'chat_proposal',
          },
        }));
        return;
      }
    }

    setExpandedAssetIndex(null);
    setIsGroupExpanded(false);
    setIsSubmitting(true);
    setSubmitError(null);
    dispatch(setProposalAssetEdits({ conversationId, messageId, edits: assetEdits.map((e) => ({ name: e.name, description: e.description })) }));
    if (proposal.assetGroup) {
      dispatch(setProposalGroupEditAction({ conversationId, messageId, edit: { name: groupEdit.name, description: groupEdit.description } }));
    }
    const overriddenProposal = {
      ...proposal,
      ...(proposal.assetGroup ? {
        assetGroup: { ...proposal.assetGroup, name: groupEdit.name, description: groupEdit.description },
      } : {}),
      assets: proposal.assets.map((a, i) => ({
        ...a,
        name: assetEdits[i].name,
        imageUrl: assetEdits[i].imageUrl ?? undefined,
        description: assetEdits[i].description,
      })),
    };
    try {
      if (isAddMode) {
        dispatch(setProposalSelectedGroup({ conversationId, messageId, groupId: selectedGroupId }));
        await approveAddProposal(overriddenProposal, selectedGroupId, messageId, dispatch, conversationId);
        dispatch(setProposalApprovedGroupId({ conversationId, messageId, groupId: selectedGroupId }));
      } else {
        const createdGroupId = await approveProposal(overriddenProposal, messageId, dispatch, conversationId);
        dispatch(setProposalApprovedGroupId({ conversationId, messageId, groupId: createdGroupId }));
      }
    } catch {
      setSubmitError('Failed to create assets. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    dispatch(setProposalStatus({ conversationId, messageId, status: 'declined' }));
  };

  return {
    status,
    isSettled,
    isAddMode,
    isSubmitting,
    submitError,
    approvedGroupId,
    groupHintNotFound,
    groupHintName,
    selectedGroupId,
    setSelectedGroupId,
    isGroupListOpen,
    setIsGroupListOpen,
    isAssetsOpen,
    setIsAssetsOpen,
    groupImageUrls,
    isGroupExpanded,
    setIsGroupExpanded,
    groupEdit,
    setGroupEdit,
    expandedAssetIndex,
    setExpandedAssetIndex,
    assetEdits,
    setAssetEdits,
    groups,
    groupList,
    handleOpenImageUpload,
    handleApprove,
    handleDecline,
  };
}
